import { observer } from 'mobx-react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Menu } from 'primereact/menu';
import { ProgressSpinner } from 'primereact/progressspinner';
import classes from './roomBpmByRunTypeTable.module.scss';
import actionIcon from 'assets/icons/PPSIcons/actionIcon.svg';
import editIcon from 'assets/icons/PPSIcons/editIcon.svg';
import deleteIcon from 'assets/icons/PPSIcons/deleteIcon.svg';
import { useRoomBpmByRunTypeTable } from './useRoomBpmByRunTypeTable';
import AddRowModalRoomBpmByRunType from './utils/AddRowModalRoomBpmByRunType';
import DeleteConfirmationModalRoomBpmByRunType from './utils/DeleteConfirmationModalRoomBpmByRunType';
import { RoomBpmByRunType } from 'Interfaces/ReferenceInterfaces/RoomBpmByRunTypeInterface';

interface RoomBpmByRunTypeTableProps {
  showAddButton?: boolean;
  showActionColumn?: boolean;
  showAllRows?: boolean;
}

interface ExpandedRowData extends RoomBpmByRunType {
  room: {
    id: number;
    room_short: string;
    room_long: string;
  };
}

const RoomBpmByRunTypeTable = observer(
  ({
    showAddButton = true,
    showActionColumn = true,
    showAllRows = true
  }: RoomBpmByRunTypeTableProps) => {
    const pageSize = showAllRows ? 100000 : 100;
    const {
      roomBpmByRunTypeData,
      isLoading,
      pagination,
      itemMenu,
      selectedRow,
      showAddModal,
      showDeleteModal,
      setSelectedRow,
      setShowAddModal,
      setShowDeleteModal,
      handleAddRow,
      handleDeleteRow,
      prepareFormData,
      roomOptions,
      runTypeOptions,
      onPageChange,
      roomTypeOptions,
      isRowDeleting,
      isSubmitting,
      handleExportToExcel
    } = useRoomBpmByRunTypeTable(pageSize);

    const menuItems = [
      {
        label: 'Edit Row',
        icon: <img src={editIcon} alt="Edit Row" style={{ marginRight: '0.5rem' }} />,
        command: () => setShowAddModal(true)
      },
      {
        label: 'Delete Row',
        icon: <img src={deleteIcon} alt="Delete Row" style={{ marginRight: '0.5rem' }} />,
        command: () => setShowDeleteModal(true)
      }
    ];

    const actionBodyTemplate = (rowData: ExpandedRowData) => {
      return (
        <Button
          icon={<img src={actionIcon} alt="Action Icon" style={{ marginRight: '0.5rem' }} />}
          className={`p-button-rounded p-button-text p-button-sm ${classes.menuButton}`}
          onClick={e => {
            // Create a modified version of the row data with only the selected room
            const selectedData: RoomBpmByRunType = {
              ...rowData,
              rooms: [rowData.room] // Only include the selected room
            };
            setSelectedRow(selectedData);
            itemMenu.current?.toggle(e);
          }}
          aria-controls="popup_menu"
          aria-haspopup
        />
      );
    };

    const runTypeTemplate = (rowData: ExpandedRowData) => {
      return rowData.job_type.description || 'Unknown';
    };

    const roomTypeTemplate = (rowData: ExpandedRowData) => {
      return rowData.room_type.description || 'Unknown';
    };

    const bpmTemplate = (rowData: ExpandedRowData) => {
      return rowData.bpm ?? '-';
    };

    if (isLoading && !roomBpmByRunTypeData.length) {
      return (
        <div className={classes.loadingContainer}>
          <ProgressSpinner className={classes.spinner} />
        </div>
      );
    }

    return (
      <div className={classes.roomBpmByRunTypeTable}>
        <div className={classes.tableHeader}>
          <h2 className={classes.sectionTitle}>Room BPM by Run Type and Job Type Configuration</h2>
          <div className={classes.headerActions}>
            <Button
              icon="pi pi-download"
              className={`${classes.addButton} p-button-icon-only`}
              onClick={handleExportToExcel}
              tooltip="Export to Excel"
              tooltipOptions={{ position: 'bottom' }}
              style={{ color: 'white !important', fontSize: '1rem' }}
            />
            {showAddButton && (
              <Button
                label="Add New Row"
                icon="pi pi-plus"
                className={classes.addButton}
                onClick={() => {
                  setSelectedRow(null);
                  setShowAddModal(true);
                }}
              />
            )}
          </div>
        </div>

        <DataTable
          value={roomBpmByRunTypeData}
          showGridlines
          stripedRows
          emptyMessage="No records found"
          className={classes.dataTable}
          loading={isLoading}
          paginator={!showAllRows}
          rows={showAllRows ? roomBpmByRunTypeData.length : 100}
          totalRecords={pagination.totalItems}
          lazy
          first={(pagination.currentPage - 1) * 100}
          onPage={onPageChange}
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
        >
          <Column field="room_type" header="Room Type" body={roomTypeTemplate} />
          <Column field="job_type" header="Job Type" body={runTypeTemplate} />
          <Column field="bpm" header="BPM" body={bpmTemplate} />
          {showActionColumn && (
            <Column
              body={actionBodyTemplate}
              style={{ width: '5rem', textAlign: 'center' }}
              headerStyle={{ width: '5rem' }}
            />
          )}
        </DataTable>

        <Menu
          model={menuItems}
          className={classes.actionMenu}
          popup
          ref={itemMenu}
          id="popup_menu"
        />

        <AddRowModalRoomBpmByRunType
          visible={showAddModal}
          onHide={() => {
            setShowAddModal(false);
            setSelectedRow(null);
          }}
          onSubmit={handleAddRow}
          initialValues={selectedRow ? prepareFormData(selectedRow) : null}
          isEdit={!!selectedRow}
          runTypeOptions={runTypeOptions}
          roomTypeOptions={roomTypeOptions}
        />

        <DeleteConfirmationModalRoomBpmByRunType
          visible={showDeleteModal}
          onHide={() => {
            setShowDeleteModal(false);
            setSelectedRow(null);
          }}
          onConfirm={handleDeleteRow}
          isDeletingLoading={isRowDeleting}
        />
      </div>
    );
  }
);

export default RoomBpmByRunTypeTable;
